import { useAuth0 } from "@/core/auth0";
import { PermissionKey } from "@/core/permissions";
import {
  PermissionEntitlements,
  useFMPermissionEntitlements,
} from "@/core/splitio";

// Product activation, campaign management and membership cancellation have no
// partner-level switch: every partner with the section has the capability, and
// who holds it is the permission alone.
const ENTITLED: Record<PermissionKey, (e: PermissionEntitlements) => boolean> =
  {
    "orders.create": (e) => e.createOrders,
    "orders.amend": (e) => e.amendOrders,
    "bookings.cancel": (e) => e.cancelBookings,
    "orders.cancel": (e) => e.cancelOrders,
    "orders.decide_requests": (e) => e.cancelOrders,
    "memberships.cancel": () => true,
    "catalog.manage": (e) => e.manageCatalog,
    "catalog.activate_products": () => true,
    "campaigns.manage": () => true,
  };

/**
 * The permission half of `useCan`: whether the signed-in user holds the
 * permission, before the partner's entitlement is asked. Owners and Flexspace
 * staff hold every permission; admins and standard users hold what was
 * granted. A profile without a permission list (from before the field existed)
 * counts as fully granted — the API is the boundary, this only decides what to
 * draw.
 */
function useHasPermission(permission: PermissionKey): boolean {
  const { userProfile } = useAuth0();
  if (!userProfile) return false;
  if (userProfile.isSuperAdmin || userProfile.role === "owner") return true;
  if (userProfile.permissions === undefined) return true;
  // The registry is what the API enforces. A key it does not serve yet cannot
  // have been withheld, so this code can ship ahead of the API that adds it.
  // Only a registry that names other keys says anything: an empty one is a
  // profile that predates the field, and the stored list decides on its own.
  const registry = userProfile.permissionRegistry ?? [];
  if (registry.length > 0 && registry.every((e) => e.key !== permission)) {
    return true;
  }
  return userProfile.permissions.includes(permission);
}

/**
 * The one door for "may the signed-in user do this": the partner must be
 * entitled to the capability and the user must hold the permission.
 */
export function useCan(permission: PermissionKey): boolean {
  const { userProfile } = useAuth0();
  const hasPermission = useHasPermission(permission);
  const entitlements = useFMPermissionEntitlements(
    userProfile?.partnerId ?? "",
    userProfile?.auth0UserId ?? ""
  );
  return ENTITLED[permission](entitlements) && hasPermission;
}
